import {
  changePassword,
  checkPassword,
  LanguageLocaleEnum,
  patchUser,
  UserPatch,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { CookiePreferenceEnum, PasswordPolicyError } from "@/config/types";
import {
  setAccessTokenStorage,
  setCookieConsentStorage,
  setRefreshTokenStorage,
} from "@/config/utils";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export function useSettings() {
  const {
    lang,
    setLang,
    user,
    setUser,
    cookieConsentFunctionalOnly,
    setCookieConsentFunctionalOnly,
    selectedMandateId,
  } = useContext(PspContext);
  const navigate = useNavigate();
  const isTablet = useIsTablet();

  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    user?.language.short,
  );

  const [cookiePreference, setCookiePreference] = useState<string>(
    cookieConsentFunctionalOnly === false
      ? CookiePreferenceEnum.ALL
      : CookiePreferenceEnum.ESSENTIAL_ONLY,
  );
  const [changePasswordDialogIsOpen, setChangePasswordDialogIsOpen] =
    useState(false);
  const [wrongPassword, setWrongPassword] = useState(false);
  const [changePasswordButtonDisabled, setChangePasswordButtonDisabled] =
    useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<
    Record<PasswordPolicyError, boolean>
  >({
    missing_digit: true,
    missing_uppercase_letter: true,
    missing_lowercase_letter: true,
    too_short: true,
    missing_symbol: true,
  });

  const somethingChanged =
    user?.language.short !== selectedLanguage ||
    (cookiePreference === CookiePreferenceEnum.ESSENTIAL_ONLY) !==
      cookieConsentFunctionalOnly;

  function onSave() {
    if (user && somethingChanged) {
      // cookies
      const functionalOnly =
        cookiePreference === CookiePreferenceEnum.ESSENTIAL_ONLY;
      setCookieConsentStorage({
        essentialOnly: functionalOnly,
      });
      setCookieConsentFunctionalOnly(functionalOnly);

      const userPatch: UserPatch = {
        language: selectedLanguage as LanguageLocaleEnum,
      };

      patchUser({ body: userPatch }).then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setLang(data.language.short);
          setUser(data);
          toast(
            translate(
              "settingsSavedSuccessfully",
              selectedLanguage as LanguageLocaleEnum,
            ),
          );
        }
      });
    }
  }

  async function onChangePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    setChangePasswordButtonDisabled(true);

    try {
      // Check old password
      const { error: checkError, status } = await checkPassword({
        body: { password: oldPassword },
      });
      if (checkError) {
        if (status === 403) setWrongPassword(true);
        else toast(translate("somethingWentWrong", lang));
        throw new Error();
      }

      // make sure the password is valid before actually making
      // a request to the API
      for (const valid of Object.values(passwordRequirements)) {
        if (valid) throw new Error("unsatisfied password requirement");
      }

      // Change password
      const { error: changeError, data: tokenData } = await changePassword({
        body: { newPassword, oldPassword },
      });
      if (changeError) {
        toast(translate("somethingWentWrong", lang));
        throw new Error();
      }

      setAccessTokenStorage(tokenData.accessToken);
      setRefreshTokenStorage(tokenData.refreshToken);

      setChangePasswordDialogIsOpen(false);
      toast(translate("passwordChangedSuccessfully", lang));
      setWrongPassword(false);
    } finally {
      setChangePasswordButtonDisabled(false);
    }
  }

  return {
    isTablet,
    navigate,
    onChangePassword,
    onSave,
    somethingChanged,
    lang,
    selectedLanguage,
    setSelectedLanguage,
    setChangePasswordDialogIsOpen,
    cookiePreference,
    setCookiePreference,
    wrongPassword,
    setWrongPassword,
    changePasswordDialogIsOpen,
    changePasswordButtonDisabled,
    passwordRequirements,
    setPasswordRequirements,
  };
}
